import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { CleaningAreaListProps } from '../../../../../../../types/CleaningAreaList.types';
import { useCleaningAreaListDnd } from '../../../../../../../hooks/useCleaningAreaListDnd';
import { CleaningAreaRowContentBlock } from './CleaningAreaRowContentBlock/CleaningAreaRowContentBlock';
import { CleaningAreaRowItem } from './CleaningAreaRowItem/CleaningAreaRowItem';
import { CleaningAreaRow } from './CleaningAreaRowItem/CleaningAreaRowItem.styles';
import {
  CleaningAreaDropIndicator,
  CleaningAreaListEmptyMessage,
  CleaningAreaOrderListContainer,
} from './CleaningAreaList.styles';

export const CleaningAreaList = ({
  areas,
  selectedAreaIds,
  onToggleArea,
  onReorder,
  disabled,
  heightPx,
}: CleaningAreaListProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    sensors,
    orderedAreas,
    activeArea,
    activeIndex,
    containerRef,
    firstRowRef,
    isAnyDragging,
    getDropIndicatorFor,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useCleaningAreaListDnd(areas, selectedAreaIds, onReorder);

  if (!areas.length) {
    return (
      <CleaningAreaOrderListContainer align="center" justify="center" style={{ height: heightPx }}>
        <CleaningAreaListEmptyMessage>{t('createSchedule.noAreasConfigured')}</CleaningAreaListEmptyMessage>
      </CleaningAreaOrderListContainer>
    );
  }

  return (
    <CleaningAreaOrderListContainer vertical ref={containerRef} style={{ height: heightPx }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={orderedAreas.map(area => area.id)} strategy={verticalListSortingStrategy}>
          {orderedAreas.map((area, index) => {
            const selectedIndex = selectedAreaIds.indexOf(area.id);
            const dropIndicator = getDropIndicatorFor(area.id);

            return (
              <Fragment key={area.id}>
                {dropIndicator === 'top' && <CleaningAreaDropIndicator />}
                <CleaningAreaRowItem
                  area={area}
                  position={selectedIndex === -1 ? null : selectedIndex}
                  onToggleArea={onToggleArea}
                  disabled={disabled}
                  measureRef={index === 0 ? firstRowRef : undefined}
                  isAnyDragging={isAnyDragging}
                />
                {dropIndicator === 'bottom' && <CleaningAreaDropIndicator />}
              </Fragment>
            );
          })}
        </SortableContext>

        <DragOverlay modifiers={[restrictToVerticalAxis]}>
          {activeArea ? (
            <CleaningAreaRow $isOverlay align="center">
              <CleaningAreaRowContentBlock
                area={activeArea}
                position={activeIndex}
                isDraggable
                onToggleArea={onToggleArea}
                disabled={disabled}
                isOverlayDragging
              />
            </CleaningAreaRow>
          ) : null}
        </DragOverlay>
      </DndContext>
    </CleaningAreaOrderListContainer>
  );
};
